// apps/admin/src/lib/auth.tsx
'use client';
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { api, setToken, getToken, setRefreshToken, clearAuth } from './api';

type Staff = { id: string; email: string; fullName?: string; platformRole?: string };
type AuthCtx = {
  staff: Staff | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
};

const Ctx = createContext<AuthCtx | null>(null);

export function StaffAuthProvider({ children }: { children: ReactNode }) {
  const [staff, setStaff] = useState<Staff | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = getToken();
    if (!t) { setLoading(false); return; }
    setToken(t);
    api.staffMe().then((r: any) => setStaff(r?.data?.staff ?? null)).catch(() => setStaff(null)).finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string) => {
    const r: any = await api.staffLogin(email, password);
    const { accessToken, refreshToken, staff: s } = r?.data ?? {};
    setToken(accessToken);
    if (refreshToken) setRefreshToken(refreshToken);
    setStaff(s);
  };

  const logout = async () => {
    try { await api.staffLogout(); } catch {}
    clearAuth();
    setToken(null);
    setStaff(null);
  };

  return <Ctx.Provider value={{ staff, loading, login, logout }}>{children}</Ctx.Provider>;
}

export function useStaffAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useStaffAuth outside StaffAuthProvider');
  return c;
}
